import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import Login from "./pages/Login";
import Dashboard from "./pages/Dashboard";
import PublicDemoSubmit from "./pages/PublicDemoSubmit";
import PublicPitch from "./pages/PublicPitch";
import { api } from "./api";
import { ConfirmProvider } from "./components/Confirm";
import "./App.css";

function AuthCallback() {
  const loc = useLocation();
  const done = useRef(false);
  const [err, setErr] = useState("");
  const [openedLogout, setOpenedLogout] = useState(false);
  useEffect(() => {
    if (done.current) return;
    done.current = true;
    const hash = loc.hash || "";
    const sid = new URLSearchParams(hash.replace(/^#/, "")).get("session_id");
    if (!sid) { setErr("Missing session_id"); return; }
    (async () => {
      try {
        await api.session(sid);
        window.history.replaceState({}, "", "/dashboard");
        window.location.replace("/dashboard");
      } catch (e) {
        setErr(String(e.message || e));
      }
    })();
  }, [loc.hash]);

  const openGoogleLogout = () => {
    window.open("https://accounts.google.com/Logout", "_blank", "noopener,noreferrer");
    setOpenedLogout(true);
  };
  const backToLogin = () => { window.location.replace("/login"); };

  return (
    <div style={{ minHeight: "100vh", display: "grid", placeItems: "center", padding: 24 }}>
      <div className="panel" style={{ padding: 32, maxWidth: 480, width: "100%", textAlign: "center" }}>
        {err ? (
          <>
            <div style={{ fontSize: 18, fontWeight: 700, marginBottom: 10 }}>Access denied</div>
            <div style={{ color: "var(--danger)", fontSize: 13, lineHeight: 1.6, marginBottom: 18 }} data-testid="auth-error">{err}</div>
            <p className="muted" style={{ fontSize: 12.5, lineHeight: 1.65, marginBottom: 18 }}>
              Google signed you in with an account that isn't on the YE Music team. Sign out of Google first, then come back and try again.
            </p>
            {!openedLogout ? (
              <button
                onClick={openGoogleLogout}
                className="btn btn-primary"
                data-testid="auth-error-switch-account"
                style={{ width: "100%", justifyContent: "center", padding: "12px 16px", fontSize: 13.5, borderRadius: 12, marginBottom: 10 }}
              >
                Step 1 — Sign out of Google
              </button>
            ) : (
              <div
                data-testid="auth-error-switch-instructions"
                style={{
                  textAlign: "left", padding: 14, borderRadius: 12, marginBottom: 12,
                  background: "rgba(124,108,255,0.06)", border: "1px solid rgba(124,108,255,0.32)",
                  fontSize: 12.5, lineHeight: 1.65,
                }}
              >
                <div style={{ fontWeight: 700, marginBottom: 6, fontSize: 13 }}>Now finish:</div>
                <ol style={{ paddingLeft: 18, margin: 0, color: "var(--muted)" }}>
                  <li>Click <b>"Sign out"</b> in the Google tab, then close it.</li>
                  <li>Come back and click <b>"Back to login"</b> below.</li>
                  <li>Enter your correct email and click Continue with Google.</li>
                </ol>
              </div>
            )}
            <button
              onClick={backToLogin}
              className="btn"
              data-testid="auth-error-back-to-login"
              style={{ width: "100%", justifyContent: "center", padding: "10px 16px", fontSize: 12.5, borderRadius: 12, background: "transparent", border: "1px solid var(--border)" }}
            >
              {openedLogout ? "Step 2 — Back to login" : "Back to login"}
            </button>
          </>
        ) : (
          <div className="muted">Signing you in…</div>
        )}
      </div>
    </div>
  );
}

function Protected({ children }) {
  const [state, setState] = useState({ loading: true, user: null });
  useEffect(() => {
    if (window.location.hash?.includes("session_id=")) { setState({ loading: false, user: null }); return; }
    api.me().then(u => setState({ loading: false, user: u })).catch(() => setState({ loading: false, user: null }));
  }, []);
  if (state.loading) return <div style={{ minHeight: "100vh", display: "grid", placeItems: "center" }} className="muted">Loading…</div>;
  if (!state.user) return <Navigate to="/login" replace />;
  return children({ user: state.user });
}

function Router() {
  const loc = useLocation();
  if (loc.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <Routes>
      <Route path="/" element={<Navigate to="/dashboard" replace />} />
      <Route path="/login" element={<Login />} />
      <Route path="/submit-demo" element={<PublicDemoSubmit />} />
      <Route path="/pitch" element={<PublicPitch />} />
      <Route path="/dashboard/*" element={<Protected>{({ user }) => <Dashboard user={user} />}</Protected>} />
    </Routes>
  );
}

export default function App() {
  return <BrowserRouter><ConfirmProvider><Router /></ConfirmProvider></BrowserRouter>;
}
